/* ==========================================================================
   Big Gun Exhaust — Digital Garage feature (preview build)
   Vanilla ES2017+. No jQuery dependency. Self-contained.
   ========================================================================== */

(function () {
  'use strict';

  // ------------------------------------------------------------------------
  // Constants
  // ------------------------------------------------------------------------
  var STORAGE_KEY = 'bge_garage_v1';
  var MAX_VEHICLES = 5;
  var VOCAB_URL = (window.BGE_GARAGE_CONFIG && window.BGE_GARAGE_CONFIG.vocabUrl) || '';
  // SEO expansion: real model collections (/collections/{make}-{model}-exhaust).
  // assets/bge-model-collections.json lists the handles that exist; until it
  // loads (or if it fails) every link falls back to the legacy tag URL.
  var MODEL_COLLECTIONS_URL = (window.BGE_GARAGE_CONFIG && window.BGE_GARAGE_CONFIG.modelCollectionsUrl) || '';
  var MODEL_COLLECTIONS = null; // { handle: true } once loaded
  // "Shop all {make} {model} parts" copy, from the Big Gun V2 theme settings
  // (layout passes it in). No copy here: if the setting is blank, no link.
  var SHOP_ALL_TEXT = (window.BGE_GARAGE_CONFIG && window.BGE_GARAGE_CONFIG.shopAllText) || '';
  // Drawer lines from Theme settings > Big Gun V2 (window.BGV2.strings); a blank setting keeps the default (FL2-04).
  function gStr(key, def) { var v = ((window.BGV2 || {}).strings || {})[key]; return v ? String(v) : def; }

  // Known canonical makes (used for tag parsing fallback when vocab makes
  // are not yet loaded). Match the migration script.
  var KNOWN_MAKES = [
    'Polaris', 'Honda', 'Yamaha', 'Can-Am', 'Can Am',
    'Arctic Cat', 'Kawasaki', 'Suzuki', 'KTM',
    'Harley-Davidson', 'Harley', 'John Deere', 'Hisun',
    'Massimo', 'Husqvarna', 'Sherco', 'GasGas', 'Textron',
    'Apollo', 'Kymco', 'Redline'
  ];
  var MAKE_CANONICAL = {
    'Can Am': 'Can-Am',
    'Harley': 'Harley-Davidson'
  };

  var YEAR_RANGE_RE = /^\d{2}-\d{2}$|^\d{4}-\d{4}$/;
  var YEAR_SINGLE_RE = /^\d{4}$|^\d{2}$/;

  // ------------------------------------------------------------------------
  // Storage helpers
  // ------------------------------------------------------------------------
  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { vehicles: [], activeId: null };
      var data = JSON.parse(raw);
      if (!data || !Array.isArray(data.vehicles)) return { vehicles: [], activeId: null };
      return data;
    } catch (e) {
      return { vehicles: [], activeId: null };
    }
  }

  function saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* swallow quota errors */ }
  }

  // ------------------------------------------------------------------------
  // Tag parser — mirrors the Python migration script logic.
  // Given a list of tag strings, returns { makes, models, yearStart, yearEnd, series }.
  // ------------------------------------------------------------------------
  var KNOWN_SERIES = ['EVO R', 'EVO U', 'EVO M', 'EVO S', 'EXO', 'EXO R', 'EXO Stainless',
                      'ECO', 'Ballistic', 'Explorer', 'WARHEAD', 'Banshee', 'Stealth'];
  var PARENT_CAT_TAGS = ['Exhausts + Pipes', 'Slip On Mufflers', 'Slip On Muffler Mufflers',
                         'Full Exhaust Systems', 'TFI Power Boxes', 'Head Pipes', 'Rev Boxes',
                         'Replacement Parts', 'Accessories', 'Gear', 'Exhaust'];
  var LEGACY_NOISE = ['woo_import_1', 'featured'];
  // Vehicle-class tags (ATV / UTV / Dirt Bike / Street from 2026-07-31, Dual-Sport
  // from 2026-09-21). They are filter facets, not models, and must not land in
  // result.models or a product with no model tag would stop matching any model.
  var VEHICLE_TYPE_TAGS = ['ATV', 'UTV', 'Dirt Bike', 'Street', 'Dual-Sport'];
  // Tags that are never a machine model. Without this, parseTags files them under models,
  // and a product with a model but no make returns no_fit for every saved vehicle (decal
  // and tip kits, packing kits, graphics kits). Compared case-insensitively.
  var NON_MODEL_TAGS = ['Decals', 'Graphics Kits', 'Packing Kits', 'Quiet Inserts', 'Spark Arrestor',
                        'Spark Arrestor Parts', 'Armor', 'Blue', 'Yellow', 'Green', 'Red', 'Black',
                        'Silver', 'Orange', 'White', 'Grey', 'Gray', 'no-dealer-price', 'duals',
                        'raptor', 'raptor700'];
  var NON_MODEL_RE = /"|\b\d+\s*pk\b/i; // inch sizes (4.25" x 4.25", 12" EXO Muffler) and pack counts (4 pk)
  function isNonModelTag(tLower) {
    for (var k = 0; k < NON_MODEL_TAGS.length; k++) {
      if (NON_MODEL_TAGS[k].toLowerCase() === tLower) return true;
    }
    return NON_MODEL_RE.test(tLower);
  }
  // Machine-only compound fitment tags (fit_yamaha-raptor-700) drive smart
  // collections. The garage reads make / model / year tags only, so skip them.
  var MACHINE_TAG_PREFIX = 'fit_';

  function parseYearToken(s) {
    s = String(s).trim();
    var m = s.match(/^(\d{2})-(\d{2})$/);
    if (m) {
      var y1 = parseInt(m[1], 10);
      var y2 = parseInt(m[2], 10);
      var full1 = y1 < 80 ? 2000 + y1 : 1900 + y1;
      var full2 = y2 < 80 ? 2000 + y2 : 1900 + y2;
      if (full2 < full1) full1 = 1900 + y1;
      return [full1, full2];
    }
    m = s.match(/^(\d{4})-(\d{4})$/);
    if (m) return [parseInt(m[1], 10), parseInt(m[2], 10)];
    m = s.match(/^(\d{4})$/);
    if (m) { var y = parseInt(m[1], 10); return [y, y]; }
    m = s.match(/^(\d{2})$/);
    if (m) { var y2d = parseInt(m[1], 10); var full = y2d < 80 ? 2000 + y2d : 1900 + y2d; return [full, full]; }
    return null;
  }

  function parseTags(tagsStr) {
    var result = { makes: [], models: [], yearStart: null, yearEnd: null, series: [], fitTags: [], byPipe: false };
    if (!tagsStr) return result;
    var tags = tagsStr.split('|').map(function (t) { return t.trim(); }).filter(Boolean);
    var yearRanges = [];
    for (var i = 0; i < tags.length; i++) {
      var t = tags[i];
      var tLower = t.toLowerCase();
      // Replacement Parts fit by pipe, not by machine (cores, packing): universal, as known fit counts them (FL3-20, FL1-30).
      if (tLower === 'replacement parts') result.byPipe = true;
      if (LEGACY_NOISE.indexOf(t) >= 0 || LEGACY_NOISE.indexOf(tLower) >= 0) continue;
      if (PARENT_CAT_TAGS.indexOf(t) >= 0) continue;
      if (tLower.indexOf(MACHINE_TAG_PREFIX) === 0) { result.fitTags.push(tLower); continue; }
      if (VEHICLE_TYPE_TAGS.indexOf(t) >= 0) continue;
      if (isNonModelTag(tLower)) continue;
      // Make matching (case-insensitive)
      var matchedMake = false;
      for (var j = 0; j < KNOWN_MAKES.length; j++) {
        if (KNOWN_MAKES[j].toLowerCase() === tLower) {
          var canonical = MAKE_CANONICAL[KNOWN_MAKES[j]] || KNOWN_MAKES[j];
          if (result.makes.indexOf(canonical) === -1) result.makes.push(canonical);
          matchedMake = true;
          break;
        }
      }
      if (matchedMake) continue;
      // Series matching (case-sensitive)
      if (KNOWN_SERIES.indexOf(t) >= 0) {
        if (result.series.indexOf(t) === -1) result.series.push(t);
        continue;
      }
      // Year range
      if (YEAR_RANGE_RE.test(t) || YEAR_SINGLE_RE.test(t)) {
        var yr = parseYearToken(t);
        if (yr) { yearRanges.push(yr); continue; }
      }
      // Otherwise model
      if (result.models.indexOf(t) === -1) result.models.push(t);
    }
    if (yearRanges.length > 0) {
      result.yearStart = Math.min.apply(null, yearRanges.map(function (y) { return y[0]; }));
      result.yearEnd = Math.max.apply(null, yearRanges.map(function (y) { return y[1]; }));
    }
    return result;
  }

  // Shopify product types that are "always universal" — packing kits, gear,
  // t-shirts, accessories. Used to distinguish actual universal products from
  // products that were merely under-tagged.
  var UNIVERSAL_PRODUCT_TYPES = ['accessories', 'gear', 'parts', 'exhaust parts'];

  // ------------------------------------------------------------------------
  // Fitment check — does this product fit the active vehicle?
  // Returns: 'fits' | 'no_fit' | 'wrong_year' | 'universal' | 'untagged' | 'unknown'
  //
  //   fits        – make/model/year all match
  //   wrong_year  – make+model match but year is out of range (amber badge)
  //   no_fit      – make or model mismatch (red badge)
  //   universal   – product has no fitment tags AND is a universal product type
  //                 (Accessories, Gear, etc). Safe to show as "Universal".
  //   untagged    – product has no fitment tags but is NOT a universal type.
  //                 Likely a data hygiene gap. Show no badge to avoid mis-promising.
  //   unknown     – no active vehicle, or vehicle missing required fields
  // ------------------------------------------------------------------------
  function checkFitment(productFitment, vehicle, productType) {
    var st = checkFitmentRaw(productFitment, vehicle, productType);
    // A by-pipe part (Replacement Parts tag) with no machine fitment reads universal, never untagged (FL3-20 CORE-BADGE):
    // the same rule as byPipe() in bge-known-fit.js, so the badge and the known-fit counts agree.
    return st === 'untagged' && productFitment.byPipe ? 'universal' : st;
  }
  function checkFitmentRaw(productFitment, vehicle, productType) {
    if (!vehicle) return 'unknown';
    var noFitData = productFitment.makes.length === 0 &&
                    productFitment.models.length === 0 &&
                    !productFitment.yearStart;
    if (noFitData) {
      var ptl = (productType || '').toLowerCase();
      return UNIVERSAL_PRODUCT_TYPES.indexOf(ptl) >= 0 ? 'universal' : 'untagged';
    }
    // Defensive: vehicle is normally populated by Garage.add() but could be
    // hand-edited via DevTools/localStorage. Treat missing make as "unknown".
    var vmake  = (vehicle.make  || '').toLowerCase();
    var vmodel = (vehicle.model || '').toLowerCase();
    if (!vmake) return 'unknown';
    // Compute make + model match independently from year
    var makeMatches = productFitment.makes.length === 0 ||
      productFitment.makes.some(function (m) { return m.toLowerCase() === vmake; });
    if (!makeMatches) return 'no_fit';
    // A machine saved without a model (home finder, drawer "Model (optional)")
    // cannot confirm a fit on a make or model specific part (CR-01).
    if (!vmodel && (productFitment.makes.length || productFitment.models.length)) return 'unknown';
    var modelMatches;
    if (productFitment.models.length) {
      modelMatches = productFitment.models.some(function (mdl) { return mdl.toLowerCase() === vmodel; });
    } else if (productFitment.makes.length) {
      // Make tag but no model tag: only a fit_<make>-<model> tag for this
      // machine confirms it. Otherwise fitment is not listed (DA-01).
      var fitTag = MACHINE_TAG_PREFIX + handleizeTag(vehicle.make) + '-' + handleizeTag(vehicle.model);
      if (!productFitment.fitTags || productFitment.fitTags.indexOf(fitTag) < 0) return 'untagged';
      modelMatches = true;
    } else {
      modelMatches = true;
    }

    // Wrong model -> hard no_fit regardless of year
    if (!modelMatches) return 'no_fit';

    // Make + model match — now check year
    if (productFitment.yearStart && productFitment.yearEnd) {
      var y = parseInt(vehicle.year, 10);
      if (isNaN(y)) return 'fits'; // year unknown — be permissive
      if (y < productFitment.yearStart || y > productFitment.yearEnd) return 'wrong_year';
    }
    return 'fits';
  }

  // Format a year range into the compact form the catalog uses ('09-13'),
  // falling back to four-digit when the range straddles centuries.
  function formatYearRange(start, end) {
    if (start >= 2000 && start < 2100 && end >= 2000 && end < 2100) {
      var s = ('0' + (start - 2000)).slice(-2);
      var e = ('0' + (end - 2000)).slice(-2);
      return s + '-' + e;
    }
    return start + '-' + end;
  }

  // ------------------------------------------------------------------------
  // SVG icons (inline, self-contained)
  // ------------------------------------------------------------------------
  var ICONS = {
    garage: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-6 9 6v11H3z"/><path d="M7 21v-9h10v9"/><path d="M7 16h10"/></svg>',
    check:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    x:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    info:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
    alert:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
    trash:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>'
  };

  // ------------------------------------------------------------------------
  // Vehicle name formatting
  // ------------------------------------------------------------------------
  function formatVehicleName(v) {
    if (!v) return '';
    return [v.year, v.make, v.model].filter(Boolean).join(' ');
  }

  function hasModel(v) { return !!(v && String(v.model || '').trim()); }

  // The one prompt for a legacy model-less machine, on every surface that names
  // the Garage. The click is delegated below and opens the drawer on that machine.
  function pickModelHtml(v, cls, label) {
    if (!v) return '';
    return '<button type="button" class="' + escapeAttr(cls || 'bgv2-btn bgv2-btn--secondary bgv2-btn--sm') + '" data-bge-action="pick-model" data-id="' + escapeAttr(v.id) + '">' +
      escapeHtml(label || 'Pick model') +
      (label && label.indexOf(String(v.year)) >= 0 ? '' : '<span class="visually-hidden"> for your ' + escapeHtml(formatVehicleName(v)) + '</span>') + '</button>';
  }

  // True while a save, switch or model pick runs on a page that re-scopes in
  // place (collection or search), so the 'bge:garage' event can say so.
  var pickInPlace = false;

  // ------------------------------------------------------------------------
  // Garage state machine
  // ------------------------------------------------------------------------
  var Garage = {
    _state: { vehicles: [], activeId: null },
    _vocab: null,
    _listeners: [],

    init: function () {
      this._state = loadState();
      this._loadVocab();
      this._loadModelCollections();
    },

    _loadModelCollections: function () {
      var self = this;
      if (!MODEL_COLLECTIONS_URL) return;
      fetch(MODEL_COLLECTIONS_URL, { credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) throw new Error('model collections HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (!data || !Array.isArray(data.handles)) {
            console.warn('[BGE Garage] Model collections file has unexpected shape');
            return;
          }
          var map = {};
          for (var i = 0; i < data.handles.length; i++) map[String(data.handles[i])] = true;
          MODEL_COLLECTIONS = map;
          // Re-render so links already on the page switch to the real collection.
          self._emit('change');
        })
        .catch(function (err) {
          console.warn('[BGE Garage] Model collections load failed:', err);
        });
    },

    _loadVocab: function () {
      var self = this;
      if (!VOCAB_URL) {
        console.warn('[BGE Garage] Vocabulary URL not configured');
        return;
      }
      fetch(VOCAB_URL, { credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) throw new Error('vocab HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          // Validate vocab shape — protects against Shopify returning a 200
          // HTML page (e.g., asset 404 redirected to storefront) that parses
          // as JSON but lacks the expected structure.
          if (!data || !Array.isArray(data.years) || !Array.isArray(data.makes) ||
              !data.models_by_make || typeof data.models_by_make !== 'object') {
            console.warn('[BGE Garage] Vocabulary file has unexpected shape');
            return;
          }
          self._vocab = data;
          self._emit('vocab_loaded');
        })
        .catch(function (err) {
          console.warn('[BGE Garage] Vocabulary load failed:', err);
        });
    },

    // Every save path requires a model (Ricky, 2026-10-05: "require"). list() and
    // active() hold only machines with a model, so no fit, no-fit, zero or tuner
    // surface ever sees a legacy model-less machine saved before the rule. Those
    // stay in storage (never deleted silently) and are reached through all() and
    // needsModel(), which feed the "Pick model" prompt.
    list: function () { return this._state.vehicles.filter(hasModel); },

    all: function () { return this._state.vehicles.slice(); },

    needsModel: function () {
      var aid = this._state.activeId;
      return this._state.vehicles.filter(function (v) { return !hasModel(v); })
        .sort(function (a, b) { return (b.id === aid) - (a.id === aid); });
    },

    // The active machine as stored, model or not (the Garage bar shows the prompt for it).
    activeRaw: function () {
      var aid = this._state.activeId;
      if (!aid) return null;
      return this._state.vehicles.find(function (v) { return v.id === aid; }) || null;
    },

    active: function () {
      var v = this.activeRaw();
      return hasModel(v) ? v : null;
    },

    add: function (year, make, model) {
      if (!String(model || '').trim()) return null;
      // Don't add duplicates — use strict equality + coerce year to number
      var yearNum = parseInt(year, 10);
      var dupe = this._state.vehicles.find(function (ex) {
        return parseInt(ex.year, 10) === yearNum && ex.make === make && (ex.model || '') === model;
      });
      if (dupe) {
        this.setActive(dupe.id);
        return dupe;
      }
      if (this._state.vehicles.length >= MAX_VEHICLES) return null;
      var id = 'v_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
      var v = { id: id, year: year, make: make, model: model };
      this._state.vehicles.push(v);
      this._state.activeId = id;
      saveState(this._state);
      this._emit('change', { type: 'save', vehicle: v });
      return v;
    },

    // A legacy model-less machine gets its model: same id, now active. If the
    // rider already saved that exact machine, the legacy entry folds into it.
    setModel: function (id, model) {
      if (!String(model || '').trim()) return null;
      var v = this._state.vehicles.find(function (x) { return x.id === id; });
      if (!v) return null;
      var dupe = this._state.vehicles.find(function (x) {
        return x.id !== id && parseInt(x.year, 10) === parseInt(v.year, 10) && x.make === v.make && x.model === model;
      });
      if (dupe) {
        this._state.vehicles = this._state.vehicles.filter(function (x) { return x.id !== id; });
        v = dupe;
      } else {
        v.model = model;
      }
      this._state.activeId = v.id;
      saveState(this._state);
      this._emit('change', { type: 'model', vehicle: v });
      return v;
    },

    remove: function (id) {
      var gone = this._state.vehicles.find(function (v) { return v.id === id; }) || null;
      this._state.vehicles = this._state.vehicles.filter(function (v) { return v.id !== id; });
      if (this._state.activeId === id) {
        var next = this._state.vehicles.filter(hasModel)[0] || this._state.vehicles[0];
        this._state.activeId = next ? next.id : null;
      }
      saveState(this._state);
      this._emit('change', { type: 'remove', vehicle: gone });
    },

    setActive: function (id) {
      var found = this._state.vehicles.find(function (v) { return v.id === id; });
      if (!found) return;
      this._state.activeId = id;
      saveState(this._state);
      this._emit('change', { type: 'switch', vehicle: found });
    },

    clearActive: function () {
      this._state.activeId = null;
      saveState(this._state);
      this._emit('change', { type: 'clear', vehicle: null });
    },

    fits: function (tagsStr) {
      var parsed = parseTags(tagsStr);
      return checkFitment(parsed, this.active());
    },

    // Best state across EVERY saved machine, the active one first so it wins a
    // tie: { state, vehicle }. A part for the rider's second machine reads
    // "Fits your <second machine>", not "Does not fit"; no_fit only when no
    // saved machine fits (fit blocker 2, 2026-10-03).
    best: function (tagsStr, productType) {
      var a = this.active();
      var vs = this.list();
      if (a) vs = [a].concat(vs.filter(function (v) { return v.id !== a.id; }));
      if (!vs.length) return { state: checkFitment(parseTags(tagsStr), null, productType), vehicle: null };
      return bestFitment(parseTags(tagsStr), vs, productType);
    },

    // Re-read storage (another tab, or a back/forward cache restore: biggun-v2.js initBfcache).
    // Emits 'sync' only when the stored Garage differs from what this page holds, so a plain
    // Back navigation re-renders nothing.
    reload: function () {
      var next = loadState();
      if (JSON.stringify(next) === JSON.stringify(this._state)) return false;
      this._state = next;
      this._emit('change', { type: 'sync', vehicle: null });
      return true;
    },

    onChange: function (cb) { this._listeners.push(cb); },

    // detail { type: save | switch | model | remove | clear | sync, vehicle } on
    // state changes. Listeners get (event, detail); the same detail goes out as
    // the DOM event 'bge:garage' on document, with active, needsModel and
    // inPlace (true when this page re-scopes in place, FE-07) added.
    _emit: function (event, detail) {
      for (var i = 0; i < this._listeners.length; i++) {
        try { this._listeners[i](event, detail); } catch (e) { /* swallow */ }
      }
      if (!detail) return;
      try {
        document.dispatchEvent(new CustomEvent('bge:garage', { detail: {
          type: detail.type, vehicle: detail.vehicle || null, active: this.active(),
          needsModel: this.needsModel(), inPlace: !!pickInPlace
        } }));
      } catch (e) { /* old browser: onChange still ran */ }
    },

    get vocab() { return this._vocab; }
  };

  // ------------------------------------------------------------------------
  // UI Renderers
  // ------------------------------------------------------------------------

  // ----- Garage bar -----
  function renderBar() {
    var bar = document.querySelector('[data-bge-garage-bar]');
    if (!bar) return;
    var active = Garage.active();
    var raw = Garage.activeRaw();
    if (raw && !active) {
      // Legacy machine saved without a model: no fit claim, one tap to finish it.
      bar.innerHTML =
        '<div class="bge-garage-bar__inner">' +
          '<span class="bge-garage-bar__icon">' + ICONS.garage + '</span>' +
          '<span class="bge-garage-bar__label">Fitment</span>' +
          '<span class="bge-garage-bar__vehicle">' + escapeHtml(formatVehicleName(raw)) + '</span>' +
          pickModelHtml(raw, 'bge-garage-bar__cta', 'Pick your model') +
          // A text button, not the ghost: on phones the bar hides a primary button that has a ghost after it (RV2-04).
          '<button type="button" class="bge-garage-bar__cta bge-garage-bar__cta--text" data-bge-action="open-drawer">Change</button>' +
        '</div>';
    } else if (active) {
      bar.innerHTML =
        '<div class="bge-garage-bar__inner">' +
          '<span class="bge-garage-bar__icon">' + ICONS.garage + '</span>' +
          '<span class="bge-garage-bar__label">Fitment</span>' +
          '<span class="bge-garage-bar__vehicle">' + escapeHtml(formatVehicleName(active)) + '</span>' +
          // Known machine: the bar's first action goes to its parts (Desktop journeys audit R5).
          '<a class="bge-garage-bar__cta" href="' + escapeAttr(buildBrowseUrl(active)) + '" data-bge-kf-click="garage_bar">Shop parts for your ' + escapeHtml(active.model || active.make) + '</a>' +
          '<button type="button" class="bge-garage-bar__cta bge-garage-bar__cta--ghost" data-bge-action="open-drawer">Change</button>' +
          '<button type="button" class="bge-garage-bar__cta bge-garage-bar__cta--text" data-bge-action="clear-active" aria-label="Clear active vehicle">Clear</button>' +
        '</div>';
    } else {
      bar.innerHTML =
        '<div class="bge-garage-bar__inner">' +
          '<span class="bge-garage-bar__icon">' + ICONS.garage + '</span>' +
          // Same shape and button label as the server markup (snippets/bge-garage-bar.liquid), so
          // nothing moves on hydrate; the line is visually hidden on phones, where it was cut off.
          '<span class="bge-garage-bar__empty"><span class="bge-garage-bar__empty-text">' + escapeHtml(((window.BGV2 || {}).strings || {}).garageBarEmpty || 'Add your machine to see parts that fit') + '</span></span>' +
          // A button once JS runs (it opens the drawer); the server's link is only the no-JS path.
          '<button type="button" class="bge-garage-bar__cta" data-bge-action="open-drawer">Find parts for your machine</button>' +
        '</div>';
    }
  }

  // ----- Garage drawer body -----
  // Id of the legacy model-less machine the drawer is finishing, or null.
  var _pickId = null;

  function renderDrawer() {
    var body = document.querySelector('[data-bge-drawer-body]');
    if (!body) return;
    var vehicles = Garage.all();
    var active = Garage.active();
    var html = '';
    var picking = _pickId ? vehicles.find(function (v) { return v.id === _pickId && !hasModel(v); }) : null;
    if (!picking) _pickId = null;

    if (picking) {
      html += '<div class="bge-garage-drawer__section bge-garage-pick">';
      html +=   '<div class="bge-garage-drawer__section-title" id="bge-pick-title">Pick your model</div>';
      html +=   '<form class="bge-ymm-form" data-bge-pick-form data-id="' + escapeAttr(picking.id) + '" aria-labelledby="bge-pick-title" novalidate>';
      html +=     '<p class="bge-garage-pick__text">Your ' + escapeHtml(formatVehicleName(picking)) + ' needs a model before we can show parts that fit it.</p>';
      html +=     '<div class="bge-ymm-field"><label class="bge-ymm-field__label" for="bge-pick-model">Model</label><select class="bge-ymm-field__select" id="bge-pick-model" data-bge-pick-model aria-describedby="bge-pick-hint"><option value="">Model</option></select></div>';
      html +=     '<p class="bge-ymm-hint" id="bge-pick-hint" data-bge-pick-hint>Pick your model</p>';
      html +=     '<button type="submit" class="bge-ymm-submit" data-bge-pick-submit disabled aria-describedby="bge-pick-hint">Save model</button>';
      html +=     '<button type="button" class="bgv2-textlink bge-garage-pick__cancel" data-bge-action="pick-cancel">Not now</button>';
      html +=   '</form>';
      html += '</div>';
    }

    if (active) {
      html += '<div class="bge-garage-drawer__section">';
      html +=   '<div class="bge-garage-drawer__section-title">Active Vehicle</div>';
      html +=   '<div class="bge-garage-vehicle-card bge-garage-vehicle-card--active">';
      var fitLabel = drawerFitLabel(active);
      if (fitLabel) html += '<div class="bge-garage-vehicle-card__label">' + fitLabel + '</div>';
      html +=     '<div class="bge-garage-vehicle-card__name">' + escapeHtml(formatVehicleName(active)) + '</div>';
      html +=     '<div class="bge-garage-vehicle-card__actions">';
      html +=       '<button type="button" class="bge-garage-vehicle-card__action" data-bge-action="browse-fit">Browse parts that fit</button>';
      html +=       '<button type="button" class="bge-garage-vehicle-card__action bge-garage-vehicle-card__action--danger" data-bge-action="remove-vehicle" data-id="' + active.id + '" aria-label="Remove ' + escapeAttr(formatVehicleName(active)) + '">Remove</button>';
      html +=     '</div>';
      html +=   '</div>';
      html += '</div>';
    }

    // Saved vehicles list (excluding active)
    var others = vehicles.filter(function (v) { return (!active || v.id !== active.id) && (!picking || v.id !== picking.id); });
    if (others.length > 0) {
      html += '<div class="bge-garage-drawer__section">';
      html +=   '<div class="bge-garage-drawer__section-title">Saved Vehicles</div>';
      html +=   '<div class="bge-garage-saved-list">';
      others.forEach(function (v) {
        var legacy = !hasModel(v);
        html += '<div class="bge-garage-saved-row' + (legacy ? ' bge-garage-saved-row--legacy' : '') + '">';
        html +=   '<div class="bge-garage-saved-row__name">' + escapeHtml(formatVehicleName(v)) +
                    (legacy ? '<span class="bge-garage-saved-row__note">Needs a model</span>' : '') + '</div>';
        html +=   '<div class="bge-garage-saved-row__actions">';
        html +=     legacy
          ? pickModelHtml(v, 'bge-garage-saved-row__btn')
          : '<button type="button" class="bge-garage-saved-row__btn" data-bge-action="set-active" data-id="' + escapeAttr(v.id) + '">Set Active</button>';
        html +=     '<button type="button" class="bge-garage-saved-row__btn bge-garage-saved-row__btn--icon" data-bge-action="remove-vehicle" data-id="' + v.id + '" aria-label="Remove ' + escapeAttr(formatVehicleName(v)) + '">' + ICONS.trash + '</button>';
        html +=   '</div>';
        html += '</div>';
      });
      html +=   '</div>';
      html += '</div>';
    }

    // Add vehicle form
    var canAdd = vehicles.length < MAX_VEHICLES;
    html += '<div class="bge-garage-drawer__section">';
    html +=   '<div class="bge-garage-drawer__section-title">' + (vehicles.length === 0 ? 'Add Your Vehicle' : 'Add Another Vehicle') + '</div>';
    if (canAdd) {
      html += '<form class="bge-ymm-form" data-bge-ymm-form>';
      html +=   '<div class="bge-ymm-field"><label class="bge-ymm-field__label" for="bge-ymm-year">Year</label><select class="bge-ymm-field__select" id="bge-ymm-year" data-bge-ymm-year aria-describedby="bge-ymm-hint"></select></div>';
      html +=   '<div class="bge-ymm-field"><label class="bge-ymm-field__label" for="bge-ymm-make">Make</label><select class="bge-ymm-field__select" id="bge-ymm-make" data-bge-ymm-make disabled aria-describedby="bge-ymm-hint"><option value="">Make</option></select></div>';
      html +=   '<div class="bge-ymm-field"><label class="bge-ymm-field__label" for="bge-ymm-model">Model</label><select class="bge-ymm-field__select" id="bge-ymm-model" data-bge-ymm-model disabled aria-describedby="bge-ymm-hint"><option value="">Model</option></select></div>';
      // Save stays disabled until a model is picked (Ricky: "require"); the hint says why.
      html +=   '<p class="bge-ymm-hint" id="bge-ymm-hint" data-bge-ymm-hint>Pick your year, make and model</p>';
      html +=   '<button type="submit" class="bge-ymm-submit" data-bge-ymm-submit disabled aria-describedby="bge-ymm-hint">' + escapeHtml(gStr('garageSubmit', 'Show my parts')) + '</button>';
      html +=   '<p class="bge-ymm-helper">Saved to this browser. Add up to ' + MAX_VEHICLES + ' vehicles to your garage.</p>';
      html += '</form>';
    } else {
      html += '<div class="bge-empty-state">Garage is full (' + MAX_VEHICLES + ' vehicles). Remove one to add another.</div>';
    }
    html += '</div>';

    body.innerHTML = html;
    if (canAdd) initYmmForm();
  }

  var YMM_INIT_MAX_RETRIES = 25; // 25 * 200ms = 5s cap before giving up
  function initYmmForm(retryCount) {
    retryCount = retryCount || 0;
    var vocab = Garage.vocab;
    if (!vocab) {
      if (retryCount < YMM_INIT_MAX_RETRIES) {
        setTimeout(function () { initYmmForm(retryCount + 1); }, 200);
      } else {
        // Give up after 5s. Surface a hint to the user inside the drawer body.
        var body = document.querySelector('[data-bge-drawer-body]');
        if (body) {
          var hint = document.createElement('div');
          hint.className = 'bge-empty-state';
          hint.textContent = 'Vehicle list unavailable. Please refresh and try again.';
          body.appendChild(hint);
        }
      }
      return;
    }
    var yearSel  = document.querySelector('[data-bge-ymm-year]');
    var makeSel  = document.querySelector('[data-bge-ymm-make]');
    var modelSel = document.querySelector('[data-bge-ymm-model]');
    var submit   = document.querySelector('[data-bge-ymm-submit]');
    var hint     = document.querySelector('[data-bge-ymm-hint]');
    if (!yearSel || !makeSel || !modelSel || !submit) return;

    // Normalize each vocab entry to {name, year_start, year_end}. Tolerates the
    // older string-only shape just in case the JSON gets swapped during an
    // update.
    function modelEntries(make) {
      var raw = (vocab.models_by_make && vocab.models_by_make[make]) || [];
      return raw.map(function (m) {
        return typeof m === 'string'
          ? { name: m, year_start: 1985, year_end: 2030 }
          : m;
      });
    }
    function modelMatchesYear(m, y) {
      return !y || (y >= m.year_start && y <= m.year_end);
    }
    function makeMatchesYear(make, y) {
      if (!y) return true;
      return modelEntries(make).some(function (m) { return modelMatchesYear(m, y); });
    }

    yearSel.innerHTML = '<option value="">Year</option>' + vocab.years.map(function (y) {
      return '<option value="' + y + '">' + y + '</option>';
    }).join('');

    function refresh() {
      var year = yearSel.value;
      var make = makeSel.value;
      var model = modelSel.value;
      makeSel.disabled = !year;
      modelSel.disabled = !year || !make;
      submit.disabled = !(year && make && model);
      submit.textContent = (year && make && model) ? gStr('garageSubmitAdd', 'Add {machine}').replace('{machine}', [year, make, model].join(' ')) : gStr('garageSubmit', 'Show my parts');
      if (hint) {
        hint.textContent = !year ? 'Pick your year, make and model' : !make ? 'Pick your make and model' : !model ? 'Pick your model' : '';
        hint.hidden = !hint.textContent;
      }
    }

    yearSel.addEventListener('change', function () {
      var y = parseInt(yearSel.value, 10);
      var prevMake  = makeSel.value;
      var prevModel = modelSel.value;
      // Cascade: only show makes that have at least one model in this year.
      var availableMakes = vocab.makes.filter(function (m) { return makeMatchesYear(m, y); });
      makeSel.innerHTML = '<option value="">Make</option>' + availableMakes.map(function (m) {
        var sel = m === prevMake ? ' selected' : '';
        return '<option value="' + escapeAttr(m) + '"' + sel + '>' + escapeHtml(m) + '</option>';
      }).join('');
      // Preserve model if previous make+model still compatible with new year
      if (prevMake && availableMakes.indexOf(prevMake) >= 0) {
        var availableModels = modelEntries(prevMake).filter(function (m) { return modelMatchesYear(m, y); });
        modelSel.innerHTML = '<option value="">Model</option>' + availableModels.map(function (m) {
          var sel = m.name === prevModel ? ' selected' : '';
          return '<option value="' + escapeAttr(m.name) + '"' + sel + '>' + escapeHtml(m.name) + '</option>';
        }).join('');
      } else {
        modelSel.innerHTML = '<option value="">Model</option>';
      }
      refresh();
    });

    makeSel.addEventListener('change', function () {
      var y = parseInt(yearSel.value, 10);
      var make = makeSel.value;
      var prevModel = modelSel.value;
      // Cascade: only show models compatible with the chosen year.
      var availableModels = modelEntries(make).filter(function (m) { return modelMatchesYear(m, y); });
      modelSel.innerHTML = '<option value="">Model</option>' + availableModels.map(function (m) {
        var sel = m.name === prevModel ? ' selected' : '';
        return '<option value="' + escapeAttr(m.name) + '"' + sel + '>' + escapeHtml(m.name) + '</option>';
      }).join('');
      refresh();
    });

    modelSel.addEventListener('change', refresh);

    var form = document.querySelector('[data-bge-ymm-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!yearSel.value || !makeSel.value || !modelSel.value) { (yearSel.value ? (makeSel.value ? modelSel : makeSel) : yearSel).focus(); return; }
        var year = parseInt(yearSel.value, 10), make = makeSel.value, model = modelSel.value;
        riderPicks(function () { return Garage.add(year, make, model); }, 'drawer');
      });
    }
  }

  // ----- Pick model for a legacy model-less machine -----
  function initPickForm(retryCount) {
    retryCount = retryCount || 0;
    var form = document.querySelector('[data-bge-pick-form]');
    if (!form) return;
    var sel = form.querySelector('[data-bge-pick-model]');
    var submit = form.querySelector('[data-bge-pick-submit]');
    var hint = form.querySelector('[data-bge-pick-hint]');
    var vocab = Garage.vocab;
    if (!vocab) {
      if (retryCount < YMM_INIT_MAX_RETRIES) setTimeout(function () { initPickForm(retryCount + 1); }, 200);
      return;
    }
    var v = Garage.all().find(function (x) { return x.id === form.getAttribute('data-id'); });
    if (!v || !sel || !submit) return;
    var y = parseInt(v.year, 10);
    var models = ((vocab.models_by_make && vocab.models_by_make[v.make]) || []).map(function (m) {
      return typeof m === 'string' ? { name: m, year_start: 1985, year_end: 2030 } : m;
    }).filter(function (m) { return !y || (y >= m.year_start && y <= m.year_end); });
    if (!models.length) {
      // Nothing to pick from (make or year no longer in the list): say so, offer the clean way out.
      sel.disabled = true;
      if (hint) hint.textContent = 'We have no models listed for a ' + formatVehicleName(v) + '. Remove it and add your machine again.';
      submit.outerHTML = '<button type="button" class="bge-ymm-submit" data-bge-action="remove-vehicle" data-id="' + escapeAttr(v.id) + '">Remove ' + escapeHtml(formatVehicleName(v)) + '</button>';
      return;
    }
    sel.innerHTML = '<option value="">Model</option>' + models.map(function (m) {
      return '<option value="' + escapeAttr(m.name) + '">' + escapeHtml(m.name) + '</option>';
    }).join('');
    var refresh = function () {
      submit.disabled = !sel.value;
      submit.textContent = sel.value ? 'Save ' + formatVehicleName({ year: v.year, make: v.make, model: sel.value }) : 'Save model';
      if (hint) { hint.textContent = sel.value ? '' : 'Pick your model'; hint.hidden = !!sel.value; }
    };
    sel.addEventListener('change', refresh);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!sel.value) { sel.focus(); return; }
      var model = sel.value;
      riderPicks(function () { return Garage.setModel(v.id, model); }, 'pick_model');
    });
    refresh();
  }

  // ------------------------------------------------------------------------
  // FE-07. Ricky: "no garage items saved, viewing collection page, i add a
  // garage item, i want the page to refresh and show me applicable items ...
  // least possible clicks to checkout." A save, a switch or a model pick:
  //   - collection page: Show all is cleared and the fit filter applies in
  //     place (no reload); bgv3-collection.js closes the filter sheet, focuses
  //     the machine chip and keeps the results in view ('bge:garage', inPlace).
  //   - search page: known fit re-scopes the results in place.
  //   - product page: stay on the product (mobile audit 2026-10-03).
  //   - anywhere else: go to the parts for that machine.
  // mutate() runs the Garage change and returns the machine, or null.
  // ------------------------------------------------------------------------
  function searchScoped() {
    return /^\/search\/?$/.test(window.location.pathname) && !!document.querySelector('.page-search [data-bge-kf-note]') &&
      !!new URLSearchParams(window.location.search).get('q');
  }
  function onProductPage() {
    return !!document.querySelector('[data-bge-pdp-fitment], [data-bgv2-fitbar]') || /\btemplate-product\b/.test(document.body.className);
  }

  function riderPicks(mutate, trigger) {
    var where = gfScope() ? 'collection' : (searchScoped() ? 'search' : (onProductPage() ? 'product' : 'other'));
    var inPlace = where === 'collection' || where === 'search';
    closeDrawer({ restoreFocus: !inPlace });
    if (inPlace) {
      // Before the change, so the first render after it is already the fit view.
      ssSet(GF_ALL_KEY, null); ssSet(GF_SEL_KEY, null); ssSet('bge_kf_all:search', null);
      gf.strict = false; gf.pageSel = null; gf.trigger = trigger;
    }
    if (inPlace) {
      // Saved from below the results (the footer finder): go back to the top of the results
      // now, with the tap, so the list swaps out of view instead of under the rider (RV2-01).
      var gridEl = document.querySelector(where === 'search' ? '.page-search .halo-column-product' : '#Collection');
      var headEl = document.querySelector(where === 'search' ? '.page-search [data-bge-kf-note], .page-search .bgv3-toolbar' : '[data-bgv3-fit], [data-bge-gf-banner]');
      if (gridEl && headEl && headEl.getBoundingClientRect().top < 0 && gridEl.getBoundingClientRect().bottom < window.innerHeight * 0.5) {
        window.scrollTo(0, Math.max(0, window.pageYOffset + headEl.getBoundingClientRect().top - 96));
      }
    }
    if (where === 'search') {
      // Reserve the note panel and hold the old grid (badged against the new machine) unpainted
      // until known fit swaps in the scoped set, as a saved machine does on a cold load (RV2-02).
      var h = document.documentElement;
      h.classList.add('bge-kf-has', 'bge-kf-pending');
      setTimeout(function () { h.classList.remove('bge-kf-pending'); }, 4000);
    }
    pickInPlace = inPlace;
    var v = null;
    try { v = mutate(); } finally { pickInPlace = false; }
    if (!v) { openDrawer(); return null; } // Garage full: the drawer says so
    if (where === 'collection') {
      // Model collections do not filter by default; a pick there means "this machine".
      if (gfScope() === 'model') gf.pageSel = [v.id];
      gfApply();
      if (!document.querySelector('[data-bgv3]')) focusLater('[data-bge-gf-banner]');
    } else if (where === 'search') {
      focusLater('.page-search [data-bge-kf-note]', '.page-search .halo-column-product');
    } else if (where === 'other') {
      window.location.href = buildBrowseUrl(v, { strict: true });
    }
    return v;
  }

  // Focus a region once it has content (no scroll jump). Used where no page
  // script owns focus after an in-place pick.
  // gridSel: when the rider saved from far below (the footer finder) and the scoped list is
  // now above the view, bring the region into view first, as the collection does (RV2-01).
  function focusLater(sel, gridSel) {
    var tries = 0;
    (function poll() {
      var el = document.querySelector(sel);
      var ready = el && !el.hidden && el.textContent.trim() && !document.body.classList.contains('bge-garage-open') &&
        !document.documentElement.classList.contains('bge-kf-pending');
      if (ready) {
        var grid = gridSel && document.querySelector(gridSel);
        var r = el.getBoundingClientRect();
        if (r.top < 0 && (!grid || grid.getBoundingClientRect().bottom < window.innerHeight * 0.5)) {
          var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: Math.max(0, window.pageYOffset + r.top - 96), behavior: reduce ? 'auto' : 'smooth' });
        }
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
        return;
      }
      if (++tries < 50) setTimeout(poll, 100);
    })();
  }

  // ----- Drawer open/close -----
  // Track the element that triggered the drawer so we can restore focus on
  // close (WCAG 2.4.3 keeps the keyboard user oriented).
  var _drawerOpenerEl = null;

  function getDrawerFocusables() {
    var drawer = document.querySelector('[data-bge-drawer]');
    if (!drawer) return [];
    var sel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(drawer.querySelectorAll(sel))
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function onDrawerKeydown(e) {
    if (e.key !== 'Tab') return;
    var focusables = getDrawerFocusables();
    if (focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // opts.pickId: open on that legacy machine's Pick model form.
  // FL2-21: the active card's eyebrow follows the page's fit state in the page's own words. "Showing parts that
  // fit" only when the list here has parts for this machine (or every part is universal); "None of these parts fit"
  // when the banner says so; "Showing all parts" when the filter is off. No parts list, still checking, or a list
  // filtered to other machines: no eyebrow.
  function drawerFitLabel(active) {
    var res = gf && gf.result; // gf is set further down; the drawer can render before it
    if (!gfScope() || !res || res.key !== gfViewKey()) return '';
    var sel = gfSelection();
    if (!sel.length) return 'Showing all parts';
    if (!sel.some(function (v) { return v.id === active.id; })) return '';
    if (res.perVehicle[active.id] > 0) return 'Showing parts that fit';
    if (!res.fits.length && res.universal.length && res.universal.length === res.total) return 'Showing parts that fit';
    return 'None of these parts fit';
  }

  function openDrawer(opts) {
    var drawer = document.querySelector('[data-bge-drawer]');
    var overlay = document.querySelector('[data-bge-overlay]');
    if (!drawer || !overlay) return;
    var wasOpen = drawer.classList.contains('is-open');
    if (!wasOpen) _drawerOpenerEl = document.activeElement;
    _pickId = opts && opts.pickId ? opts.pickId : null;
    renderDrawer();
    if (_pickId) initPickForm();
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.classList.add('bge-garage-open');
    drawer.setAttribute('aria-hidden', 'false');
    // inert only while closed: a hidden drawer must not keep its buttons in the tab order (axe aria-hidden-focus)
    drawer.inert = false; drawer.removeAttribute('inert');
    drawer.addEventListener('keydown', onDrawerKeydown);
    // Move focus into the drawer so screen-readers + keyboard users land here.
    // setTimeout gives the transition a beat to complete first.
    setTimeout(function () {
      var pick = _pickId && document.querySelector('[data-bge-pick-model]');
      if (pick) { pick.focus(); return; }
      var focusables = getDrawerFocusables();
      if (focusables.length > 0) focusables[0].focus();
    }, 50);
  }

  // opts.restoreFocus false: the page moves focus itself (FE-07 in-place pick).
  function closeDrawer(opts) {
    var restore = !(opts && opts.restoreFocus === false);
    _pickId = null;
    var drawer = document.querySelector('[data-bge-drawer]');
    var overlay = document.querySelector('[data-bge-overlay]');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      drawer.removeEventListener('keydown', onDrawerKeydown);
    }
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('bge-garage-open');
    // Restore focus to whatever opened the drawer.
    if (restore && _drawerOpenerEl && typeof _drawerOpenerEl.focus === 'function' && document.contains(_drawerOpenerEl)) {
      try { _drawerOpenerEl.focus(); } catch (e) { /* element may be gone */ }
    }
    _drawerOpenerEl = null;
  }

  // ----- Fitment badges on product cards -----
  // Most popular once per class and series in a grid, on the cards a rider can see (FL4-08 call 87, FL5-02, call 88 rev).
  // The server marks the first card of each key data-bgv2-pop and the later ones data-bgv2-pop-spare (hidden). Cards
  // built in JS (garage filter, known fit, ?view=bge-card) carry no dedupe, and a hide can take the badge card away, so
  // after every insert, show or hide the first visible card of each key wears it and the rest hide theirs.
  function cardShown(item) {
    return !!item && !item.hidden && !item.hasAttribute('data-bge-gf-hidden') && !item.hasAttribute('data-bge-kf-hidden');
  }
  function popOnce() {
    var grids = document.querySelectorAll('.halo-column-product');
    for (var g = 0; g < grids.length; g++) {
      var seen = {};
      // A one-class page (data-bgv2-pop-cls) gives no badge to a card of another class (call 87).
      var cls = grids[g].getAttribute('data-bgv2-pop-cls');
      var tags = grids[g].querySelectorAll('[data-bgv2-pop], [data-bgv2-pop-spare]');
      for (var i = 0; i < tags.length; i++) {
        var t = tags[i];
        var k = t.getAttribute('data-bgv2-pop') || t.getAttribute('data-bgv2-pop-spare');
        var on = !seen[k] && (!cls || k.split(':')[0] === cls) && cardShown(t.closest('.halo-item'));
        if (on) seen[k] = 1;
        // Write only what changes, so the watcher below sees no churn.
        if (on && t.getAttribute('data-bgv2-pop') !== k) { t.setAttribute('data-bgv2-pop', k); t.removeAttribute('data-bgv2-pop-spare'); }
        if (!on && t.getAttribute('data-bgv2-pop-spare') !== k) { t.setAttribute('data-bgv2-pop-spare', k); t.removeAttribute('data-bgv2-pop'); }
        if (t.hidden === on) t.hidden = !on;
      }
    }
  }
  // Runs before paint after any card insert or remove, or a garage filter or known fit hide or show, in the grids.
  function watchPop() {
    popOnce();
    if (!window.MutationObserver) return;
    // popOnce writes only data-bgv2-pop, data-bgv2-pop-spare and hidden (outside the filter, no childList), and any
    // record from its own writes is dropped, so it never re-triggers itself.
    var mo = new MutationObserver(function () { popOnce(); mo.takeRecords(); });
    document.querySelectorAll('.halo-column-product').forEach(function (grid) {
      mo.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-bge-gf-hidden', 'data-bge-kf-hidden'] });
    });
  }

  function renderFitmentBadges() {
    // Badges follow the garage filter: with machines selected, each card is
    // badged against the selected machine it fits best, so a part for the
    // second machine reads "Fits Your RZR XP 1000", not "Does Not Fit".
    // Known-fit lists (assets/bge-known-fit.js, marked data-bge-kf-on) are
    // scoped to every saved machine, so their cards are badged the same way.
    var selected = gfSelection();
    var allVehicles = Garage.list();
    // A model page with no machine chip picked does not filter (gfSelection is []), and its fit bar counts every
    // saved machine ("Your 2019 Yamaha Raptor 700: 14 of 22 parts fit"). The cards follow the same machines, each
    // badged against the saved machine it fits best, as on a hub; the active machine alone (a RZR on a Raptor
    // page) marked every card Does Not Fit under that line (FL1-18).
    var modelAll = !selected.length && gfScope() === 'model' && allVehicles.length;
    var pageVehicles = selected.length ? selected : (modelAll ? allVehicles : (Garage.active() ? [Garage.active()] : []));
    var cards = document.querySelectorAll('[data-bge-tags]');
    var shopShown = []; // [carousel, vehicle id] pairs that already carry the Shop all link (N-A23)
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var vehicles = allVehicles.length && card.closest('[data-bge-kf-on]') ? allVehicles : pageVehicles;
      var multi = vehicles.length > 1;
      var tags = card.getAttribute('data-bge-tags') || '';
      var productType = card.getAttribute('data-bge-type') || '';
      var parsed = parseTags(tags);
      var best = vehicles.length ? bestFitment(parsed, vehicles, productType) : { state: checkFitment(parsed, null, productType), vehicle: null };
      var state = best.state;
      var active = best.vehicle;

      // Find or create badge slot
      var slot = card.querySelector('.bge-fitment-badge-slot');
      if (!slot) {
        var imageWrap = card.querySelector('.product-image') || card;
        slot = document.createElement('div');
        slot.className = 'bge-fitment-badge-slot';
        imageWrap.appendChild(slot);
      }

      // Untagged: under-tagged product — don't promise universal fit. No badge.
      // Unknown: no active vehicle.
      if (state === 'unknown' || state === 'untagged') {
        slot.innerHTML = '';
        continue;
      }
      if (state === 'universal' && !active) {
        // Don't clutter cards with universal badges when no garage
        slot.innerHTML = '';
        continue;
      }
      // Badge, plus (fits only) the "Shop all {make} {model} parts" link to the
      // real model collection. Both live in the absolutely positioned badge
      // slot over the image, so nothing in the card reflows when JS fills it.
      // In a carousel (recently viewed, slick) the link shows once per machine, on its first fitting card (N-A23, DESIGN 6).
      var shop = state === 'fits' && active;
      var deck = shop && card.closest('[data-slick], .slick-slider');
      if (deck && card.closest('.slick-cloned')) shop = false; // a clone never claims the link ahead of the real slide
      else if (deck) {
        var seen = shopShown.some(function (p) { return p[0] === deck && p[1] === active.id; });
        if (seen) shop = false; else shopShown.push([deck, active.id]);
      }
      slot.innerHTML = badgeHtml(state, active, parsed, multi) + (shop ? shopAllLinkHtml(active, 'bgv2-card-shop-all') : '');
      // A card in a hidden carousel slide (recently viewed, slick) keeps its new links out of the tab order (FL2-16).
      if (card.closest('[aria-hidden="true"]')) slot.querySelectorAll('a, button').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
    }
  }

  // "Shop all {Make} {Model} parts" -> the real model collection. Empty string
  // when no such collection exists or we are already on it.
  function shopAllLinkHtml(vehicle, cls) {
    var url = modelCollectionUrl(vehicle);
    if (!url || !SHOP_ALL_TEXT || onModelCollectionPage(vehicle)) return '';
    var text = SHOP_ALL_TEXT.replace('{make}', vehicle.make).replace('{model}', vehicle.model || '');
    return '<a class="' + cls + '" href="' + escapeAttr(url) + '">' + escapeHtml(text.replace(/\s+/g, ' ').trim()) + '</a>';
  }

  function badgeHtml(state, vehicle, productFitment, multi) {
    var label, cls, icon;
    switch (state) {
      case 'fits':
        label = vehicle ? 'Fits Your ' + (multi ? gfModelName(vehicle) : (vehicle.year || '')) : 'Fits';
        cls = 'bge-fitment-badge--fits';
        icon = ICONS.check;
        break;
      case 'wrong_year':
        if (productFitment && productFitment.yearStart && productFitment.yearEnd) {
          label = 'Fits ' + formatYearRange(productFitment.yearStart, productFitment.yearEnd);
        } else {
          label = 'Different Year';
        }
        cls = 'bge-fitment-badge--wrong-year';
        icon = ICONS.alert;
        break;
      case 'no_fit':
        label = 'Does Not Fit';
        cls = 'bge-fitment-badge--no-fit';
        icon = ICONS.x;
        break;
      case 'universal':
        label = 'Universal';
        cls = 'bge-fitment-badge--universal';
        icon = '';
        break;
      default:
        return '';
    }
    return '<span class="bge-fitment-badge ' + cls + '">' + (icon || '') + ' ' + escapeHtml(label) + '</span>';
  }

  // ----- PDP fitment panel -----
  function renderPdpFitmentPanel() {
    var panel = document.querySelector('[data-bge-pdp-fitment]');
    if (!panel) return;
    var tags = panel.getAttribute('data-bge-tags') || '';
    var productType = panel.getAttribute('data-bge-type') || '';
    var parsed = parseTags(tags);
    var current = Garage.active();
    var best = Garage.best(tags, productType);
    var state = best.state;
    var active = best.vehicle || current;
    var other = !!(state === 'fits' && active && (!current || active.id !== current.id));

    var classes = ['bge-pdp-fitment'];
    var label, title, action = '', icon;
    switch (state) {
      case 'fits':
        classes.push('bge-pdp-fitment--fits');
        label = 'Fitment Confirmed';
        title = 'Fits your ' + formatVehicleName(active);
        icon = ICONS.check;
        // A text link to every part for the saved machine: the model collection when
        // one exists, otherwise the filtered browse URL.
        action = '<a class="bgv2-textlink bge-pdp-fitment__seeall" href="' + escapeAttr(modelCollectionUrl(active) || buildBrowseUrl(active, { strict: true })) + '">See all parts for your ' + escapeHtml(formatVehicleName(active)) + '</a>';
        // It fits a saved machine that is not the active one: offer to switch.
        if (other) action = '<button type="button" class="bge-pdp-fitment__action bge-pdp-fitment__action--ghost" data-bge-action="set-active" data-id="' + escapeAttr(active.id) + '">Make it my active machine</button>' + action;
        break;
      case 'wrong_year':
        classes.push('bge-pdp-fitment--wrong-year');
        label = 'Year Does Not Match';
        if (parsed.yearStart && parsed.yearEnd) {
          title = 'This part fits ' + formatYearRange(parsed.yearStart, parsed.yearEnd) +
                  ' ' + active.make + (active.model ? ' ' + active.model : '') +
                  '. Your ' + active.year + ' is outside this range.';
        } else {
          title = 'Year does not match your ' + formatVehicleName(active);
        }
        icon = ICONS.alert;
        action =
          '<a class="bge-pdp-fitment__action bge-pdp-fitment__action--ghost" href="' + escapeAttr(buildBrowseUrl(active, { strict: true })) + '">Show parts that fit my ' + escapeHtml(active.make + (active.model ? ' ' + active.model : '')) + '</a>' +
          '<button type="button" class="bgv2-textlink bge-pdp-fitment__change" data-bge-action="open-drawer">Change vehicle</button>';
        break;
      case 'no_fit':
        classes.push('bge-pdp-fitment--no-fit');
        label = 'Fitment Issue';
        title = Garage.list().length > 1 ? 'Does not fit any of your saved machines' : 'Does not fit your ' + formatVehicleName(active);
        icon = ICONS.x;
        action =
          '<a class="bge-pdp-fitment__action bge-pdp-fitment__action--ghost" href="' + escapeAttr(buildBrowseUrl(active, { strict: true })) + '">Show parts that fit my ' + escapeHtml(active.make + (active.model ? ' ' + active.model : '')) + '</a>' +
          '<button type="button" class="bgv2-textlink bge-pdp-fitment__change" data-bge-action="open-drawer">Change vehicle</button>';
        break;
      case 'universal':
        classes.push('bge-pdp-fitment--universal');
        label = 'Universal Fitment';
        title = 'Fits all vehicles';
        icon = ICONS.info;
        break;
      case 'untagged':
        classes.push('bge-pdp-fitment--unknown');
        label = 'Fitment Not Specified';
        title = 'Contact us to confirm this part fits your ' + formatVehicleName(active);
        icon = ICONS.alert;
        action = '<a class="bge-pdp-fitment__action bge-pdp-fitment__action--primary" href="mailto:sales@biggunexhaust.com?subject=Fitment%20question%20-%20' + encodeURIComponent(document.title) + '">Ask About Fitment</a>';
        break;
      default:
        classes.push('bge-pdp-fitment--unknown');
        var legacy = Garage.needsModel()[0];
        label = 'Will this fit your ' + (panel.getAttribute('data-bge-class') || 'machine') + '?';
        icon = ICONS.alert;
        if (legacy && !Garage.list().length && checkFitment(parsed, legacy, productType) === 'no_fit') {
          // Another make than the part: no model of it can fit (call 73).
          classes.push('bge-pdp-fitment--no-fit');
          label = 'Fitment Issue';
          title = 'Does not fit your ' + formatVehicleName(legacy);
          icon = ICONS.x;
          action = '<button type="button" class="bgv2-textlink bge-pdp-fitment__change" data-bge-action="open-drawer">Change vehicle</button>';
        } else if (legacy && !Garage.list().length) {
          // Saved before a model was required: no fit claim until the model is picked.
          title = 'Pick the model of your ' + formatVehicleName(legacy) + ' to confirm fitment';
          action = pickModelHtml(legacy, 'bge-pdp-fitment__action bge-pdp-fitment__action--primary', 'Pick your model');
        } else {
          title = 'Add your vehicle to confirm fitment';
          action = '<button type="button" class="bge-pdp-fitment__action bge-pdp-fitment__action--primary" data-bge-action="open-drawer">+ Add Vehicle</button>';
        }
        break;
    }
    panel.className = classes.join(' ');
    panel.innerHTML =
      '<div class="bge-pdp-fitment__icon">' + icon + '</div>' +
      '<div class="bge-pdp-fitment__body">' +
        '<div class="bge-pdp-fitment__label">' + label + '</div>' +
        '<div class="bge-pdp-fitment__title">' + escapeHtml(title) + '</div>' +
        action +
      '</div>';
  }

  // ------------------------------------------------------------------------
  // Garage filter (collection pages)
  // Design and research: Strategy/2026-10/garage-filter/Garage-Filter-Design-2026-10-03.md
  //
  // With a machine saved, hub and part collections show only the parts that
  // fit it (plus universal parts), across the WHOLE collection view, not just
  // the loaded page: a 2019 Raptor 700 fits 16 of ATV Exhaust's 646 parts,
  // spread over 10 of its 16 pages, so page-only hiding left most pages empty.
  //
  //   1. Index: <view>?view=bge-fit (templates/collection.bge-fit.liquid)
  //      returns [id, handle, tags, type] for every product in the view, with
  //      the shopper's tag filters and sort applied.
  //   2. Match: checkFitment, OR across the selected machines. Kept: fits,
  //      then universal. Hidden: no_fit, wrong_year, untagged.
  //   3. Cards: cards on the page are cloned; the rest come from
  //      /products/<handle>?view=bge-card (templates/product.bge-card.liquid).
  //      Server-rendered cards stay in the DOM, hidden, so "Show all" restores
  //      the page exactly. Crawlers never run this, so the indexable HTML is
  //      unchanged.
  //
  // Selection: all saved machines by default on hubs; none by default on model
  // collections (already one machine). "Show all" holds for the session. A
  // ?bge_strict=1 link means "the active machine only". Zero guard: if nothing
  // in the view fits, nothing is hidden and the banner says so.
  // No URL change, so GA4 page_view stays the clean collection URL.
  // ------------------------------------------------------------------------
  var GF_ALL_KEY = 'bge_gf_all';      // '1' = Show all, for the session
  var GF_SEL_KEY = 'bge_gf_sel';      // JSON ids ticked in the sidebar (hub pages)
  var GF_IDX_PREFIX = 'bge_gf_idx:';  // cached index per view
  var GF_IDX_TTL = 10 * 60 * 1000;
  var GF_CARD_CONCURRENCY = 6;
  var GF_RANK = { fits: 3, wrong_year: 2, universal: 1, no_fit: 0, untagged: 0, unknown: -1 };

  var gf = {
    strict: /[?&]bge_strict=1(&|$)/.test(window.location.search),
    pageSel: null,   // model pages: ids ticked on this page only
    run: 0,          // latest apply wins
    gen: 0,          // bumped when the theme swaps the grid
    appliedSig: '',
    result: null,
    list: [], nFits: 0, shown: 0, pageSize: 24, originals: {},
    idx: {}, cards: {}, parsed: {}, tracked: {},
    trigger: 'default'
  };

  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) {
    try { if (v == null) window.sessionStorage.removeItem(k); else window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ }
  }

  function gfBlock() { return document.querySelector('[data-bge-gf-block]'); }
  function gfScope() { var b = gfBlock(); return b ? b.getAttribute('data-scope') : null; }
  function gfGrid() { return document.querySelector('#Collection .halo-column-product'); }

  // The machines the grid is filtered to right now. [] = show everything.
  function gfSelection() {
    var vs = Garage.list();
    var scope = gfScope();
    if (!vs.length || !scope) return [];
    if (gf.strict) { var a = Garage.active(); return a ? [a] : []; }
    if (scope === 'model') {
      return gf.pageSel ? vs.filter(function (v) { return gf.pageSel.indexOf(v.id) >= 0; }) : [];
    }
    if (ssGet(GF_ALL_KEY) === '1') return [];
    var sel = null;
    try { sel = JSON.parse(ssGet(GF_SEL_KEY) || 'null'); } catch (e) { sel = null; }
    if (Array.isArray(sel)) {
      var picked = vs.filter(function (v) { return sel.indexOf(v.id) >= 0; });
      if (picked.length) return picked;
    }
    return vs;
  }

  function gfSetSelection(ids, trigger) {
    var vs = Garage.list();
    gf.strict = false; // the rider took over from a ?bge_strict=1 link
    if (gfScope() === 'model') {
      gf.pageSel = ids.length ? ids : null;
    } else if (!ids.length) {
      ssSet(GF_ALL_KEY, '1'); ssSet(GF_SEL_KEY, null);
    } else {
      ssSet(GF_ALL_KEY, null);
      ssSet(GF_SEL_KEY, ids.length === vs.length ? null : JSON.stringify(ids));
    }
    if (!ids.length) gfTrack('garage_filter_show_all', { trigger: trigger, collection: window.location.pathname });
    gf.trigger = trigger;
    gfApply();
  }

  // A new machine starts a fresh selection: every saved machine, Show all off.
  function gfResetSelection() {
    ssSet(GF_ALL_KEY, null); ssSet(GF_SEL_KEY, null);
    gf.strict = false; gf.pageSel = null;
  }

  function gfViewKey() {
    var u = new URL(window.location.href);
    var p = new URLSearchParams();
    ['constraint', 'sort_by'].forEach(function (k) { var v = u.searchParams.get(k); if (v) p.set(k, v); });
    var q = p.toString();
    return u.pathname + (q ? '?' + q : '');
  }

  function gfFetchIndex(key) {
    if (gf.idx[key]) return gf.idx[key];
    var cached = null;
    try { cached = JSON.parse(ssGet(GF_IDX_PREFIX + key) || 'null'); } catch (e) { cached = null; }
    if (cached && Array.isArray(cached.rows) && Date.now() - cached.t < GF_IDX_TTL) {
      return (gf.idx[key] = Promise.resolve(cached.rows));
    }
    var base = key + (key.indexOf('?') >= 0 ? '&' : '?') + 'view=bge-fit';
    var page = function (n) {
      return fetch(base + '&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('index HTTP ' + r.status); return r.text(); })
        .then(function (t) {
          var m = t.match(/<script type="application\/json" id="bge-fit-index">([\s\S]*?)<\/script>/);
          if (!m) throw new Error('index has unexpected shape');
          return JSON.parse(m[1]);
        });
    };
    var p = page(1).then(function (first) {
      var rest = [];
      for (var n = 2; n <= first.pages; n++) rest.push(page(n));
      return Promise.all(rest).then(function (more) {
        var rows = first.products;
        more.forEach(function (d) { rows = rows.concat(d.products); });
        var entry = JSON.stringify({ t: Date.now(), rows: rows });
        try { window.sessionStorage.setItem(GF_IDX_PREFIX + key, entry); } catch (e) {
          // Quota: drop older indexes, keep this one if it fits.
          try {
            for (var i = window.sessionStorage.length - 1; i >= 0; i--) {
              var k = window.sessionStorage.key(i);
              if (k && k.indexOf(GF_IDX_PREFIX) === 0) window.sessionStorage.removeItem(k);
            }
            window.sessionStorage.setItem(GF_IDX_PREFIX + key, entry);
          } catch (e2) { /* run uncached */ }
        }
        return rows;
      });
    });
    p.catch(function () { delete gf.idx[key]; });
    return (gf.idx[key] = p);
  }

  function gfParsed(row) {
    var id = row[0];
    if (!gf.parsed[id]) gf.parsed[id] = parseTags(row[2]);
    return gf.parsed[id];
  }

  // Best state for one product across several machines (fits beats other year).
  function bestFitment(parsed, vehicles, productType) {
    var best = { state: 'unknown', vehicle: null };
    for (var i = 0; i < vehicles.length; i++) {
      var s = checkFitment(parsed, vehicles[i], productType);
      if (GF_RANK[s] > GF_RANK[best.state]) best = { state: s, vehicle: vehicles[i] };
    }
    return best;
  }

  function gfMatch(rows, sel) {
    var vs = Garage.list();
    var res = { total: rows.length, fits: [], universal: [], wrongYear: 0, perVehicle: {}, anyFit: 0 };
    vs.forEach(function (v) { res.perVehicle[v.id] = 0; });
    var selIds = sel.map(function (v) { return v.id; });
    rows.forEach(function (r) {
      var parsed = gfParsed(r);
      var fitSel = false, wrongSel = false, universal = false, fitAny = false;
      vs.forEach(function (v) {
        var s = checkFitment(parsed, v, r[3]);
        var inSel = selIds.indexOf(v.id) >= 0;
        if (s === 'fits') { res.perVehicle[v.id]++; fitAny = true; if (inSel) fitSel = true; }
        else if (s === 'wrong_year' && inSel) wrongSel = true;
        else if (s === 'universal') universal = true;
      });
      if (fitAny) res.anyFit++;
      if (fitSel) res.fits.push(r);
      else if (universal && selIds.length) res.universal.push(r);
      else if (wrongSel) res.wrongYear++;
    });
    return res;
  }

  // ----- Cards -----
  var gfQueue = [], gfActive = 0;
  function gfPump() {
    while (gfActive < GF_CARD_CONCURRENCY && gfQueue.length) {
      var job = gfQueue.shift();
      gfActive++;
      job.fn().then(function (v) { gfActive--; this.resolve(v); gfPump(); }.bind(job),
                    function (e) { gfActive--; this.reject(e); gfPump(); }.bind(job));
    }
  }
  function gfLimit(fn) {
    return new Promise(function (resolve, reject) { gfQueue.push({ fn: fn, resolve: resolve, reject: reject }); gfPump(); });
  }
  // view: 'bge-card' (collection grid card) or 'bge-card-grid' (the home and
  // search card, templates/product.bge-card-grid.liquid).
  function gfCardHtml(handle, view) {
    view = view || 'bge-card';
    var k = view + ':' + handle;
    if (!gf.cards[k]) {
      gf.cards[k] = gfLimit(function () {
        return fetch('/products/' + encodeURIComponent(handle) + '?view=' + view, { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('card HTTP ' + r.status); return r.text(); });
      });
      gf.cards[k].catch(function () { delete gf.cards[k]; });
    }
    return gf.cards[k];
  }
  function gfCard(row) {
    var orig = gf.originals[String(row[0])];
    if (orig) {
      var c = orig.cloneNode(true);
      c.removeAttribute('data-bge-gf-hidden');
      c.setAttribute('data-bge-gf-added', '');
      return Promise.resolve(c);
    }
    return gfCardHtml(row[1]).then(function (html) {
      var t = document.createElement('template');
      t.innerHTML = html;
      var item = t.content.querySelector('.halo-item');
      return item ? document.importNode(item, true) : null;
    }, function () { return null; });
  }

  function gfClear() {
    var grid = gfGrid();
    if (grid) {
      grid.querySelectorAll('[data-bge-gf-added]').forEach(function (el) { el.remove(); });
      grid.querySelectorAll('[data-bge-gf-hidden]').forEach(function (el) { el.removeAttribute('data-bge-gf-hidden'); });
    }
    document.documentElement.classList.remove('bge-gf-on');
    gf.list = []; gf.nFits = 0; gf.shown = 0;
    gfFoot();
  }

  function gfRender(res, token) {
    var grid = gfGrid();
    if (!grid) return Promise.resolve();
    var originals = {}, n = 0;
    Array.prototype.forEach.call(grid.children, function (el) {
      if (!el.classList.contains('halo-item') || el.hasAttribute('data-bge-gf-added')) return;
      var card = el.querySelector('[data-id]');
      if (card) originals[card.getAttribute('data-id')] = el;
      n++;
    });
    gf.originals = originals;
    gf.pageSize = Math.max(n, 24);
    gf.list = res.fits.concat(res.universal);
    gf.nFits = res.fits.length;
    gf.shown = 0;
    // The swap waits for the first batch of cards: hiding the server cards first left
    // the grid empty while cards were fetched, and the bottom repeat jumped into view
    // and back (RV-01, CLS 0.12 at 390 with 3 machines).
    return gfShowMore(token, function () {
      grid.querySelectorAll('[data-bge-gf-added]').forEach(function (el) { el.remove(); });
      Array.prototype.forEach.call(grid.children, function (el) {
        if (el.classList.contains('halo-item')) el.setAttribute('data-bge-gf-hidden', '');
      });
      document.documentElement.classList.add('bge-gf-on');
      gfFoot();
    });
  }

  function gfShowMore(token, before) {
    var slice = gf.list.slice(gf.shown, gf.shown + gf.pageSize);
    var start = gf.shown;
    return Promise.all(slice.map(gfCard)).then(function (nodes) {
      if (token !== gf.run) return;
      var grid = gfGrid();
      if (!grid) return;
      if (before) before();
      var more = grid.querySelector('.bge-gf-more');
      if (more) more.remove();
      // Grouped model pages (Slip-ons, Full systems...): each card goes back under its own group
      // header, not after the last one, so the headers do not stand empty (CR-18).
      var grouped = grid.hasAttribute('data-bgv3-grouped');
      nodes.forEach(function (node, i) {
        // the divider only separates machine parts from universal ones; with no machine parts the banner says it
        if (start + i === gf.nFits && gf.nFits > 0 && gf.nFits < gf.list.length) grid.appendChild(gfDivider());
        if (!node) return;
        var key = grouped ? node.getAttribute('data-bgv3-in') : '';
        var head = key ? grid.querySelector('.bgv3-group[data-bgv3-group="' + key + '"]') : null;
        if (!head) { grid.appendChild(node); return; }
        var after = head;
        for (var sib = head.nextElementSibling; sib && !sib.classList.contains('bgv3-group'); sib = sib.nextElementSibling) {
          if (sib.hasAttribute('data-bge-gf-added') && sib.getAttribute('data-bgv3-in') === key) after = sib;
        }
        after.parentNode.insertBefore(node, after.nextSibling);
      });
      gf.shown = start + slice.length;
      var left = gf.list.length - gf.shown;
      if (left > 0) {
        var m = document.createElement('div');
        m.className = 'halo-item halo-item-full bge-gf-more';
        m.setAttribute('data-bge-gf-added', '');
        m.innerHTML = '<button type="button" class="bgv2-btn bgv2-btn--secondary" data-bge-action="gf-more">Show ' + Math.min(left, gf.pageSize) + ' more ' + plural(Math.min(left, gf.pageSize), 'part') + '</button>';
        grid.appendChild(m);
      }
      renderFitmentBadges();
      try { if (window.theme && window.theme.wishlist && typeof window.theme.wishlist.init === 'function') window.theme.wishlist.init(); } catch (e) { /* optional */ }
    });
  }

  function gfDivider() {
    var d = document.createElement('div');
    d.className = 'halo-item halo-item-full bge-gf-divider';
    d.setAttribute('data-bge-gf-added', '');
    d.innerHTML = '<p class="bge-gf-divider__title">Universal parts</p><p class="bge-gf-divider__note">Not tied to one machine.</p>';
    return d;
  }

  // ----- Copy -----
  function plural(n, word) { return n === 1 ? word : word + 's'; }
  function gfNames(sel) {
    return sel.length === 1 ? formatVehicleName(sel[0]) : sel.length + ' machines';
  }
  function gfModelName(v) { return v.model || v.make; }

  // One "Shop <machine> parts" button per saved machine in the selection (every machine, not the first two)
  function gfShopEach(sel) {
    return sel.map(function (v) {
      return '<a class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" href="' + escapeAttr(buildBrowseUrl(v)) + '">Shop ' + escapeHtml(v.make + (v.model ? ' ' + v.model : '')) + ' parts</a>';
    }).join('');
  }

  // ----- Banner, bottom repeat, status -----
  function gfBanner(mode, sel, res) {
    var banner = document.querySelector('[data-bge-gf-banner]');
    var status = document.querySelector('[data-bge-gf-status]');
    var canAdd = Garage.all().length < MAX_VEHICLES;
    var addBtn = canAdd ? '<button type="button" class="bgv2-textlink" data-bge-action="open-drawer">Add a machine</button>' : '';
    var title = '', note = '', actions = '', say = '';

    if (mode === 'loading') {
      title = 'Finding parts that fit your ' + escapeHtml(gfNames(sel));
    } else if (mode === 'fit') {
      var n = res.fits.length;
      // Name only the machines with parts here; say which ones have none.
      var withFits = sel.filter(function (v) { return res.perVehicle[v.id] > 0; });
      var noFits = sel.filter(function (v) { return !res.perVehicle[v.id]; });
      title = n + ' ' + plural(n, 'part') + ' fit your ' + escapeHtml(gfNames(withFits.length ? withFits : sel));
      var notes = [];
      if (noFits.length) notes.push('None fit your ' + escapeHtml(noFits.map(formatVehicleName).join(' or ')) + ' here.');
      if (res.universal.length) notes.push('Plus ' + res.universal.length + ' universal ' + plural(res.universal.length, 'part') + '.');
      if (res.wrongYear) notes.push(res.wrongYear + ' more fit other ' + (sel.length === 1 ? escapeHtml(gfModelName(sel[0])) + ' ' : '') + 'years.');
      note = notes.join(' ');
      actions = '<button type="button" class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" data-bge-action="gf-show-all">Show all ' + res.total + ' parts</button>' + addBtn;
      say = title + '. ' + note;
    } else if (mode === 'zero') {
      title = 'None of these parts fit your ' + escapeHtml(gfNames(sel));
      if (res.wrongYear && sel.length === 1) note = res.wrongYear + ' ' + plural(res.wrongYear, 'part') + ' here fit other ' + escapeHtml(gfModelName(sel[0])) + ' years.';
      actions = gfShopEach(sel) + addBtn;
      say = title + '. ' + note;
    } else if (mode === 'universal') {
      var nu = res.universal.length;
      // The visible fit bar's universal line (bgv3-collection.js universalLine): universal parts are never said
      // to fit a machine (call 67, FL1-22). "Fit by muffler" only when every part is a by-pipe part (FL2-16).
      var strs = (window.BGV2 && window.BGV2.strings) || {};
      var pipe = res.universal.every(function (r) { return /(^|\|)\s*replacement parts\s*(\||$)/i.test(r[2] || '') || String(r[3] || '').trim().toLowerCase() === 'exhaust parts'; });
      title = escapeHtml(pipe ? (strs.fitByPipe || 'These parts are not machine specific. They fit by muffler, not by machine.') : (strs.fitUniversal || 'These parts are not machine specific.'));
      actions = (nu < res.total ? '<button type="button" class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" data-bge-action="gf-show-all">Show all ' + res.total + ' parts</button>' : '') + gfShopEach(sel) + addBtn;
      say = title;
    } else if (mode === 'all') {
      var vs = Garage.list();
      title = 'Showing all ' + (res ? res.total + ' ' : '') + 'parts';
      actions = '<button type="button" class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" data-bge-action="gf-fit">Show parts for my ' + escapeHtml(vs.length === 1 ? gfModelName(vs[0]) : 'machines') + '</button>' + addBtn;
    }

    if (banner) {
      if (!title) {
        banner.hidden = true; banner.innerHTML = '';
      } else {
        banner.hidden = false;
        banner.setAttribute('data-mode', mode);
        banner.innerHTML =
          '<div class="bge-gf-banner__icon">' + ICONS.garage + '</div>' +
          '<div class="bge-gf-banner__body">' +
            '<p class="bge-gf-banner__title">' + title + '</p>' +
            (note ? '<p class="bge-gf-banner__note">' + note + '</p>' : '') +
            (actions ? '<div class="bge-gf-banner__actions">' + actions + '</div>' : '') +
          '</div>';
      }
    }
    gf.foot = (mode === 'fit' || (mode === 'universal' && res && res.universal.length < res.total)) ? addBtn : null;
    gfFoot();
    if (status && mode !== 'loading') status.textContent = say.replace(/<[^>]+>/g, '');
  }

  // Bottom repeat: only once the filtered grid is in place (bge-gf-on), so it never
  // shows under an empty grid mid-swap.
  function gfFoot() {
    var foot = document.querySelector('[data-bge-gf-foot]');
    if (!foot) return;
    if (gf.foot != null && document.documentElement.classList.contains('bge-gf-on')) {
      var h = '<p class="bge-gf-foot__text">You are viewing parts that fit your garage.</p>' +
        '<div class="bge-gf-banner__actions"><button type="button" class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" data-bge-action="gf-show-all">Show all parts</button>' + gf.foot + '</div>';
      if (foot.__h !== h) { foot.innerHTML = h; foot.__h = h; }
      foot.hidden = false;
    } else if (!foot.hidden || foot.__h) {
      foot.hidden = true; foot.innerHTML = ''; foot.__h = '';
    }
  }

  // A one-make machine page (a model collection, or a fit_ tag page such as utv-exhaust/fit_honda-pioneer-700): a
  // model-less machine of another make can fit nothing here, so it gets the none-fit line, not the model prompt
  // (call 73 on collections, FL5-09). Multi-make pages and a machine of the page's make keep the prompt (call 7).
  function pageMakes() {
    var root = document.querySelector('[data-bgv3]');
    var model = gfScope() === 'model' || (root && root.getAttribute('data-scope') === 'model');
    var fitTag = /^\/collections\/[^/]+\/[^?#]*fit_/i.test(window.location.pathname) ||
      /(^|[+ ])fit_/i.test(new URLSearchParams(window.location.search).get('constraint') || '');
    if (!model && !fitTag) return null;
    var makes = [];
    document.querySelectorAll('#Collection [data-bge-tags]').forEach(function (c) {
      parseTags(c.getAttribute('data-bge-tags') || '').makes.forEach(function (m) { if (makes.indexOf(m) < 0) makes.push(m); });
    });
    return makes.length === 1 ? makes[0] : null;
  }
  // The model-less machine the prompt is for: the first of this page's make (or any, off one-make pages); null when
  // every one is another make.
  function legacyForPage(legacy) {
    var make = pageMakes();
    if (!make) return legacy[0] || null;
    return legacy.filter(function (v) { return String(MAKE_CANONICAL[v.make] || v.make || '').toLowerCase() === make.toLowerCase(); })[0] || null;
  }

  // ----- Sidebar block -----
  function gfRenderBlock(res) {
    var block = gfBlock();
    var body = block && block.querySelector('[data-bge-gf-body]');
    if (!body) return;
    block.hidden = false;
    var vs = Garage.list();
    var legacy = Garage.needsModel();
    if (!vs.length) {
      var pick = legacyForPage(legacy);
      body.innerHTML = pick
        ? '<p class="bge-gf-block__empty">Your ' + escapeHtml(formatVehicleName(pick)) + ' needs a model before we can show the parts that fit.</p>' + pickModelHtml(pick)
        : (legacy.length
          ? '<p class="bge-gf-block__empty">None of these fit your ' + escapeHtml(formatVehicleName(legacy[0])) + '.</p>'
          : '<p class="bge-gf-block__empty">Save your machine to see only the parts that fit.</p>') +
          '<button type="button" class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" data-bge-action="open-drawer">Add a machine</button>';
      return;
    }
    var focusId = document.activeElement && body.contains(document.activeElement) ? document.activeElement.id : '';
    var selIds = gfSelection().map(function (v) { return v.id; });
    // An all-universal view (packing kits, gear) shows no machine counts: "5" beside the Raptor read as a fit claim (N-A21).
    var uniAll = !!(res && res.universal.length && res.universal.length === res.total);
    var count = function (n) {
      if (!res || uniAll) return '';
      // A machine with nothing here reads as words, not a bare "0" (coordinator, FE-09 follow-up).
      return n ? '<span class="bge-gf-row__count">' + n + '<span class="visually-hidden"> ' + plural(n, 'part') + '</span></span>'
        : '<span class="bge-gf-row__count bge-gf-row__count--none">No parts here</span>';
    };
    // Universal parts join a machine's count only beside parts that fit it: a machine with no fits here never reads
    // "3" for three stickers (FL2-19, ST-03 shape).
    var withUni = function (n) { return res && n ? n + res.universal.length : n; };
    var html = '<fieldset class="bge-gf-list"><legend class="visually-hidden">Show only parts that fit</legend>';
    if (vs.length > 1) {
      html += '<div class="bge-gf-row bge-gf-row--all"><input type="checkbox" id="bge-gf-all" data-bge-gf-all' +
        (selIds.length === vs.length ? ' checked' : '') + '><label for="bge-gf-all"><span class="bge-gf-row__name">All my machines</span>' +
        count(res ? withUni(res.anyFit) : 0) + '</label></div>';
    }
    vs.forEach(function (v) {
      var id = 'bge-gf-v-' + v.id;
      html += '<div class="bge-gf-row' + (vs.length > 1 ? ' bge-gf-row--sub' : '') + '"><input type="checkbox" id="' + escapeAttr(id) + '" value="' + escapeAttr(v.id) + '" data-bge-gf-v' +
        (selIds.indexOf(v.id) >= 0 ? ' checked' : '') + '><label for="' + escapeAttr(id) + '"><span class="bge-gf-row__name">' + escapeHtml(formatVehicleName(v)) + '</span>' +
        count(res ? withUni(res.perVehicle[v.id] || 0) : 0) + '</label></div>';
    });
    html += '</fieldset>';
    legacy.forEach(function (v) {
      html += '<div class="bge-gf-row bge-gf-row--legacy' + (vs.length > 1 ? ' bge-gf-row--sub' : '') + '"><span class="bge-gf-row__name">' + escapeHtml(formatVehicleName(v)) + '</span>' +
        pickModelHtml(v, 'bgv2-textlink bge-gf-row__pick') + '</div>';
    });
    html += '<button type="button" class="bgv2-textlink bge-gf-block__edit" data-bge-action="open-drawer">Edit my garage</button>';
    body.innerHTML = html;
    var all = body.querySelector('[data-bge-gf-all]');
    if (all) all.indeterminate = selIds.length > 0 && selIds.length < vs.length;
    if (focusId) { var f = document.getElementById(focusId); if (f) f.focus(); }
  }

  function onGfChange(e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest('[data-bge-gf-block]')) return;
    var vs = Garage.list();
    if (t.hasAttribute('data-bge-gf-all')) {
      gfSetSelection(t.checked ? vs.map(function (v) { return v.id; }) : [], 'sidebar');
      return;
    }
    if (!t.hasAttribute('data-bge-gf-v')) return;
    var ids = Array.prototype.map.call(document.querySelectorAll('[data-bge-gf-block] [data-bge-gf-v]:checked'), function (el) { return el.value; });
    gfSetSelection(ids, 'sidebar');
  }

  // GA4 through the store's Google channel (same nil-safe helper as biggun-v2.js).
  function gfTrack(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) { /* ignore */ }
  }

  function gfApply(force) {
    var html = document.documentElement;
    var scope = gfScope();
    if (!scope) { html.classList.remove('bge-gf-pending'); return; }
    var vs = Garage.list();
    var sel = gfSelection();
    var key = gfViewKey();
    var sig = [key, gf.gen, vs.map(function (v) { return v.id + ':' + v.year; }).join(','), sel.map(function (v) { return v.id; }).join(',')].join('|');
    if (!force && sig === gf.appliedSig) { gfRenderBlock(gf.result); renderFitmentBadges(); return; }
    gf.appliedSig = sig;
    var token = ++gf.run;

    if (!vs.length) {
      gf.result = null;
      gfClear(); gfBanner('off'); gfRenderBlock(null);
      html.classList.remove('bge-gf-pending', 'bge-gf-reserve');
      return;
    }
    if (sel.length) { html.classList.add('bge-gf-reserve'); gfBanner('loading', sel); }
    gfRenderBlock(gf.result && gf.result.key === key ? gf.result : null);

    gfFetchIndex(key).then(function (rows) {
      if (token !== gf.run) return;
      var res = gfMatch(rows, sel);
      res.key = key;
      gf.result = res;
      gfRenderBlock(res);
      var done = function () { if (token === gf.run) html.classList.remove('bge-gf-pending'); };
      var trackKey = sig + '|' + res.fits.length;
      if (!sel.length) {
        gfClear();
        gfBanner(scope === 'hub' ? 'all' : 'off', sel, res);
        if (scope !== 'hub') html.classList.remove('bge-gf-reserve');
        renderFitmentBadges();
        return done();
      }
      // Zero guard (fit blocker 4): nothing tied to these machines, but universal
      // parts (packing kits, inserts) fit every machine. Show those under a
      // banner that says so and names every machine, never "None fit". Only when
      // every part in view is universal: a hub with a few stickers and no fits for
      // the machine takes the zero guard below and shows everything (ST-03).
      if (!res.fits.length && res.universal.length && res.universal.length === res.total) {
        gfBanner('universal', sel, res);
        if (!gf.tracked[trackKey]) { gf.tracked[trackKey] = 1; gfTrack('garage_filter_apply', { trigger: gf.strict ? 'strict_link' : gf.trigger, collection: window.location.pathname, machines: sel.length, results: 0, universal: res.universal.length, total: res.total }); }
        return gfRender(res, token).then(done, done);
      }
      if (!res.fits.length) {
        gfClear();
        gfBanner('zero', sel, res);
        renderFitmentBadges();
        if (!gf.tracked[trackKey]) { gf.tracked[trackKey] = 1; gfTrack('garage_filter_zero', { collection: window.location.pathname, machines: sel.length, other_years: res.wrongYear }); }
        return done();
      }
      gfBanner('fit', sel, res);
      if (!gf.tracked[trackKey]) {
        gf.tracked[trackKey] = 1;
        gfTrack('garage_filter_apply', { trigger: gf.strict ? 'strict_link' : gf.trigger, collection: window.location.pathname, machines: sel.length, results: res.fits.length, universal: res.universal.length, total: res.total });
      }
      return gfRender(res, token).then(done, done);
    }).catch(function (err) {
      if (token !== gf.run) return;
      console.warn('[BGE Garage] Garage filter index failed:', err);
      gf.appliedSig = '';
      gfClear(); gfBanner('off'); renderFitmentBadges();
      html.classList.remove('bge-gf-pending', 'bge-gf-reserve');
    });
  }

  // ------------------------------------------------------------------------
  // Event delegation
  // ------------------------------------------------------------------------
  function onClick(e) {
    var t = e.target;
    while (t && t !== document.body) {
      var action = t.getAttribute && t.getAttribute('data-bge-action');
      if (action) {
        handleAction(action, t, e);
        return;
      }
      t = t.parentNode;
    }
  }

  function handleAction(action, el, e) {
    if (action === 'open-drawer')      { e.preventDefault(); openDrawer(); }
    else if (action === 'close-drawer'){ e.preventDefault(); closeDrawer(); }
    else if (action === 'clear-active'){ e.preventDefault(); Garage.clearActive(); }
    else if (action === 'pick-model')  { e.preventDefault(); openDrawer({ pickId: el.getAttribute('data-id') }); }
    else if (action === 'pick-cancel') { e.preventDefault(); _pickId = null; renderDrawer(); var f = getDrawerFocusables()[0]; if (f) f.focus(); }
    else if (action === 'set-active')  {
      e.preventDefault();
      var sid = el.getAttribute('data-id');
      // Switching on a collection or search page re-scopes it in place (FE-07); elsewhere the drawer stays open.
      if (el.closest('[data-bge-drawer]') && (gfScope() || searchScoped())) riderPicks(function () { Garage.setActive(sid); return Garage.active(); }, 'switch');
      else { var spot = drawerSpot(el); Garage.setActive(sid); if (spot) { renderDrawer(); refocusDrawer(spot); } }
    }
    else if (action === 'remove-vehicle'){ e.preventDefault(); var rspot = drawerSpot(el); Garage.remove(el.getAttribute('data-id')); renderDrawer(); if (rspot) refocusDrawer(rspot); }
    else if (action === 'browse-fit')  { e.preventDefault(); closeDrawer(); window.location.href = buildBrowseUrl(Garage.active(), { strict: true }); }
    else if (action === 'gf-show-all') { e.preventDefault(); gfSetSelection([], el.closest('[data-bge-gf-foot]') ? 'bottom' : 'banner'); }
    else if (action === 'gf-fit')      { e.preventDefault(); gfSetSelection(Garage.list().map(function (v) { return v.id; }), 'banner'); }
    else if (action === 'gf-more')     { e.preventDefault(); el.setAttribute('aria-busy', 'true'); gfShowMore(gf.run); }
  }

  // FL1-08: renderDrawer() replaces the body, so the pressed button is gone. Note where it was (its action and
  // saved row) and put focus on the same control in that row afterwards, else the row above, else the next
  // control left in the drawer, else its heading; never <body>, where the Tab trap no longer holds.
  function drawerSpot(el) {
    if (!el.closest('[data-bge-drawer]')) return null;
    var row = el.closest('.bge-garage-saved-row');
    var rows = Array.prototype.slice.call(document.querySelectorAll('[data-bge-drawer] .bge-garage-saved-row'));
    return { action: el.getAttribute('data-bge-action'), row: row ? rows.indexOf(row) : -1 };
  }
  function refocusDrawer(spot) {
    var d = document.querySelector('[data-bge-drawer]');
    if (!d) return;
    var rows = d.querySelectorAll('.bge-garage-saved-row');
    var pick = function (row) { return row && (row.querySelector('[data-bge-action="' + spot.action + '"]') || row.querySelector('button')); };
    var target = spot.row >= 0 ? (pick(rows[spot.row]) || pick(rows[spot.row - 1])) : null;
    if (!target) target = d.querySelector('.bge-garage-vehicle-card__action, .bge-garage-saved-row button, [data-bge-ymm-year]');
    if (!target) { target = d.querySelector('.bge-garage-drawer__title'); if (target) target.setAttribute('tabindex', '-1'); }
    if (target) target.focus();
  }

  function onKey(e) {
    if (e.key === 'Escape') closeDrawer();
  }

  // ------------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------------

  // Shopify tag-handle: lowercase, replace non-alphanum runs with hyphens, trim.
  // Matches Shopify's internal handleize() so /collections/all/<handle> works.
  function handleizeTag(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Build a Shopify path-based tag filter URL for the active vehicle.
  // opts.strict=true  → append ?bge_strict=1 so client-side filter hides
  //                     wrong-year/no-fit cards (used by "Show parts that fit").
  // opts.makeOnly=true→ omit model from path (looser, make-only filter).
  // Falls back to /collections/all when no vehicle/make.
  // Maps dropdown canonical → catalog tag form when they differ
  // (catalog tag is "Harley" but dropdown ships "Harley-Davidson", etc).
  // Real model collection for a vehicle, or null when none exists.
  // Handle = handleize(make) + '-' + handleize(model) + '-exhaust', matching
  // the fit_{make}-{model} tag convention behind those smart collections.
  function modelCollectionHandle(vehicle) {
    if (!MODEL_COLLECTIONS || !vehicle || !vehicle.make || !vehicle.model) return null;
    var h = handleizeTag(vehicle.make) + '-' + handleizeTag(vehicle.model) + '-exhaust';
    return MODEL_COLLECTIONS[h] ? h : null;
  }
  function modelCollectionUrl(vehicle) {
    var h = modelCollectionHandle(vehicle);
    return h ? '/collections/' + h : null;
  }
  function onModelCollectionPage(vehicle) {
    var h = modelCollectionHandle(vehicle);
    return !!h && window.location.pathname.indexOf('/collections/' + h) === 0;
  }

  function buildBrowseUrl(vehicle, opts) {
    opts = opts || {};
    if (!vehicle || !vehicle.make) return '/collections/all';
    // Prefer the real model collection when one exists (SEO expansion). The
    // strict flag still applies: the client-side year filter runs on any
    // collection page.
    if (!opts.makeOnly) {
      var real = modelCollectionUrl(vehicle);
      if (real) return real + (opts.strict ? '?bge_strict=1' : '');
    }
    var make = vehicle.make;
    var TAG_OVERRIDES = { 'Harley-Davidson': 'Harley', 'Can-Am': 'Can Am' };
    if (TAG_OVERRIDES[make]) make = TAG_OVERRIDES[make];
    var handles = [handleizeTag(make)];
    if (vehicle.model && !opts.makeOnly) handles.push(handleizeTag(vehicle.model));
    var url = '/collections/all/' + handles.join('+');
    if (opts.strict) url += '?bge_strict=1';
    return url;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // ------------------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------------------
  function boot() {
    Garage.init();
    watchPop();
    document.addEventListener('click', onClick, false);
    document.addEventListener('keydown', onKey, false);

    // Cross-tab sync — if the user updates their garage in another tab, refresh
    // this tab's state. Storage events fire in OTHER tabs, not the originating
    // one, so this won't cause echo loops.
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) Garage.reload();
    });

    document.addEventListener('change', onGfChange, false);

    Garage.onChange(function () {
      renderBar();
      renderFitmentBadges();
      renderPdpFitmentPanel();
      gfApply();
    });

    // Initial render after vocab loads (so badges have data)
    var initial = function () {
      renderBar();
      renderFitmentBadges();
      renderPdpFitmentPanel();
      gfApply();
    };
    initial();
    // Also re-render after vocab loads (in case bar/badge rendering depended on it)
    setTimeout(initial, 400);

    // Re-render when the theme changes the grid (its AJAX tag filter swaps
    // #Collection and the sidebar; infinite scroll appends cards). Cards the
    // garage filter adds itself carry data-bge-gf-added and are ignored.
    var observer = new MutationObserver(function (mutations) {
      var shouldRerender = false, sidebarSwapped = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          for (var j = 0; j < mutations[i].addedNodes.length; j++) {
            var n = mutations[i].addedNodes[j];
            if (n.nodeType !== 1 || (n.hasAttribute && (n.hasAttribute('data-bge-gf-added') || n.hasAttribute('data-bge-kf-added')))) continue;
            if (n.matches && n.matches('[data-bge-gf-block]') || n.querySelector && n.querySelector('[data-bge-gf-block]')) sidebarSwapped = true;
            if (n.matches && n.matches('[data-bge-tags]') ||
                n.querySelector && n.querySelector('[data-bge-tags]')) {
              shouldRerender = true;
            }
          }
        }
      }
      if (shouldRerender) {
        gf.gen++;
        // Runs before the next paint: hold the new grid until it is filtered.
        if (gfSelection().length) document.documentElement.classList.add('bge-gf-pending');
        renderFitmentBadges();
        gfApply();
      } else if (sidebarSwapped) {
        gfApply(true);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for debugging
  window.BGE = window.BGE || {};
  window.BGE.Garage = Garage;
  window.BGE.openGarage = openDrawer;
  window.BGE.closeGarage = closeDrawer;
  window.BGE.parseTags = parseTags;
  window.BGE.checkFitment = checkFitment;
  // Read by the V2 nav (biggun-v2.js) so the drawer and mega panels show the saved machine
  window.BGE.browseUrl = buildBrowseUrl;
  window.BGE.formatVehicle = formatVehicleName;
  // Legacy model-less machines: the shared "Pick model" button (opens the drawer on it).
  window.BGE.pickModelHtml = pickModelHtml;
  window.BGE.openPickModel = function (id) { openDrawer({ pickId: id }); };
  // Garage filter (collection pages), for QA
  window.BGE.garageFilter = { apply: gfApply, selection: gfSelection, state: gf };
  // Known fit across the journey (assets/bge-known-fit.js) reuses the same
  // feed: index(path) -> [id, handle, tags, type, available] rows for every
  // product in a collection or search view; best(parsed, vehicles, type) ->
  // { state, vehicle }; card(handle, view) -> rendered card HTML.
  window.BGE.fitFeed = { index: gfFetchIndex, best: bestFitment, card: gfCardHtml };
  window.BGE.renderBadges = renderFitmentBadges;
  window.BGE.popOnce = popOnce;
  window.BGE.legacyForPage = legacyForPage;
  window.BGE.modelUrl = modelCollectionUrl;
})();
